import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { ArrowDown, ArrowUp } from '@ringcentral/juno-icon';
import { RcFade } from '../Transitions/Fade';
import { RcGrow } from '../Transitions/Grow';
import { combineClasses, combineProps, getParsePaletteColor, omit, styled, useDeprecatedCheck, useEventCallback, useForkRef, useTheme, useThemeProps, usePrevious, } from '../../foundation';
import { RcChip } from '../Chip';
import { ClearIconButton } from '../Forms/TextField/styles/ClearIconButton';
import { RcVisuallyHidden } from '../VisuallyHidden';
import { ArrowDownButton, DownshiftStyle, EndAdornment, RcDownshiftInput, StyledPopper, StyledTextField, } from './styles';
import { RcSuggestionList } from './SuggestionList';
import { DEFAULT_GET_OPTION_LABEL, DEFAULT_KEY_TO_CHIPS, DEFAULT_LIMIT_CHIPS, RcDownshiftDefaultFilterOptions, RcDownshiftInputClasses, useDownshift, useDownshiftError, } from './utils';
/**
 * default transition style for grow when not virtualize
 */
var DEFAULT_GROW_STYLE = { style: { transformOrigin: '0 0 0' } };
var _RcDownshift = memo(forwardRef(function (inProps, ref) {
    var props = useThemeProps({ props: inProps, name: 'RcDownshift' });
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDeprecatedCheck(RcDownshift, props, [
            {
                prop: 'onSelectChange',
                time: '2021-10',
                comment: "@deprecated should use `onChange` */",
            },
            {
                prop: 'suggestionItems',
                time: '2021-10',
                comment: "@deprecated  should use `options` to replace */",
            },
            {
                prop: 'selectedItems',
                time: '2021-10',
                comment: "@deprecated  not need any more */",
            },
            {
                prop: 'itemToString',
                time: '2021-10',
                comment: "@deprecated use `getOptionLabel` to replace that */",
            },
            {
                prop: 'MenuItem',
                time: '2021-10',
                comment: "@deprecated should use `renderOption` to replace that */",
            },
            {
                prop: 'minRowHeight',
                time: '2021-10',
                comment: "@deprecated suggestion list row min height for virtual list */",
            },
            {
                prop: 'enableFreeChips',
                time: '2021-10',
                comment: "@deprecated enable free chip mode, should use `freeSolo` to replace that */",
            },
            {
                prop: 'inputLabel',
                time: '2021-10',
                comment: "@deprecated please use `label` to replace that */",
            },
            {
                prop: 'inputPlaceholder',
                time: '2021-10',
                comment: "@deprecated input element placeholder attribute */",
            },
            {
                prop: 'maxLength',
                time: '2021-10',
                comment: "@deprecated input element maxlength, please use `TextFieldProps.inputProps.maxLength` */",
            },
            {
                prop: 'screenReader',
                time: '2021-10',
                comment: "@deprecated should use `screenReaderProps` */",
            },
            {
                prop: 'automationId',
                time: '2021-10',
                comment: "@deprecated wrapper automationId, should use `data-test-automation-id` directly */",
            },
            {
                prop: 'InputItem',
                time: '2021-10',
                comment: "@deprecated just use the `renderTags` to render whole tags  */",
            },
            {
                prop: 'emailError',
                time: '2021-10',
                comment: "@deprecated please calculate error outside by your self with `renderTags`, or prepare ready items with error field */",
            },
            {
                prop: 'errorSelectedItems',
                time: '2021-10',
                comment: "@deprecated please calculate error outside by your self with `renderTags`, or prepare ready items with error field */",
            },
            {
                prop: 'nameError',
                time: '2021-10',
                comment: "@deprecated replace to `error` */",
            },
            {
                prop: 'keyToChips',
                time: '2021-10',
                comment: "@deprecated rename to `keyToTags` */",
            },
            {
                prop: 'limitOfFreeChips',
                time: '2021-10',
                comment: "@deprecated should use `max` */",
            },
            {
                prop: 'enableAutoTransform',
                time: '2021-10',
                comment: "@deprecated should use `autoSelect` */",
            },
            {
                prop: 'messageRef',
                time: '2021-10',
                comment: "@deprecated should use `inputRef` */",
            },
            {
                prop: 'TextFieldProps',
                time: '2021-10',
                comment: "@deprecated TextField props */",
            },
        ]);
    }
    var theme = useTheme();
    var isAutocomplete = props.variant === 'autocomplete';
    var _a = props.itemToString, itemToString = _a === void 0 ? DEFAULT_GET_OPTION_LABEL : _a, _b = props.keyToChips, keyToChips = _b === void 0 ? DEFAULT_KEY_TO_CHIPS : _b, _c = props.limitOfFreeChips, limitOfFreeChips = _c === void 0 ? DEFAULT_LIMIT_CHIPS : _c, minRowHeight = props.minRowHeight, suggestionItems = props.suggestionItems, messageRef = props.messageRef, MenuItem = props.MenuItem, inputLabel = props.inputLabel, nameError = props.nameError, maxLength = props.maxLength, InputItem = props.InputItem, emailError = props.emailError, autoSwitchEmail = props.autoSwitchEmail, enableFreeChips = props.enableFreeChips, inputPlaceholder = props.inputPlaceholder, errorSelectedItems = props.errorSelectedItems, enableAutoTransform = props.enableAutoTransform, allowPlainHelperText = props.allowPlainHelperText, TextFieldProps = props.TextFieldProps, selectedItemsProp = props.selectedItems, automationId = props.automationId, screenReader = props.screenReader, onSelectChange = props.onSelectChange, variant = props.variant, groupExpanded = props.groupExpanded, groupDefaultExpanded = props.groupDefaultExpanded, _d = props.groupVariant, groupVariant = _d === void 0 ? 'normal' : _d, getExpandIconProps = props.getExpandIconProps, groupBy = props.groupBy, _e = props.value, valueProp = _e === void 0 ? selectedItemsProp : _e, _f = props.autoHighlight, autoHighlight = _f === void 0 ? true : _f, _g = props.fullWidth, fullWidth = _g === void 0 ? true : _g, _h = props.clearBtn, clearBtn = _h === void 0 ? false : _h, _j = props.toggleButton, toggleButton = _j === void 0 ? false : _j, _k = props.multiple, multiple = _k === void 0 ? false : _k, clearButtonProps = props.clearButtonProps, onClear = props.onClear, ToggleButtonProps = props.ToggleButtonProps, _l = props.getToggleButtonProps, getToggleButtonPropsProp = _l === void 0 ? function (isOpen) {
        return {
            symbol: isOpen ? ArrowUp : ArrowDown,
        };
    } : _l, inputValueProp = props.inputValue, onKeyDownProp = props.onKeyDown, helperTextProp = props.helperText, options = props.options, openOnFocus = props.openOnFocus, _m = props.toggleWithInput, toggleWithInput = _m === void 0 ? isAutocomplete : _m, _o = props.getOptionLabel, getOptionLabel = _o === void 0 ? itemToString : _o, renderInput = props.renderInput, _p = props.label, label = _p === void 0 ? inputLabel : _p, _q = props.inputRef, inputRefProp = _q === void 0 ? messageRef : _q, _r = props.error, error = _r === void 0 ? nameError : _r, _s = props.placeholder, placeholder = _s === void 0 ? inputPlaceholder : _s, _t = props.freeSolo, freeSolo = _t === void 0 ? enableFreeChips : _t, _u = props.keyToTags, keyToTags = _u === void 0 ? keyToChips : _u, _v = props.maxFreeSolo, maxFreeSolo = _v === void 0 ? limitOfFreeChips : _v, _w = props.SuggestionListProps, _x = _w === void 0 ? {} : _w, _y = _x.virtualize, virtualize = _y === void 0 ? true : _y, SuggestionListProps = __rest(_x, ["virtualize"]), _z = props.autoSelect, autoSelect = _z === void 0 ? enableAutoTransform : _z, PopperComponent = props.PopperComponent, _0 = props.PopperProps, _1 = _0 === void 0 ? {} : _0, _2 = _1.anchorElType, anchorElType = _2 === void 0 ? 'root' : _2, popperTransition = _1.transition, _3 = _1.TransitionComponent, TransitionComponent = _3 === void 0 ? virtualize ? RcFade : RcGrow : _3, _4 = _1.transitionDuration, transitionDurationProp = _4 === void 0 ? 'auto' : _4, _5 = _1.TransitionProps, TransitionPropsProp = _5 === void 0 ? {} : _5, PopperProps = __rest(_1, ["anchorElType", "transition", "TransitionComponent", "transitionDuration", "TransitionProps"]), initialIsOpen = props.initialIsOpen, disabled = props.disabled, requiredProp = props.required, _6 = props.defaultIsOpen, defaultIsOpen = _6 === void 0 ? openOnFocus : _6, _7 = props.disableCloseOnSelect, disableCloseOnSelect = _7 === void 0 ? defaultIsOpen : _7, _8 = props.onChange, onChangeProp = _8 === void 0 ? onSelectChange : _8, onInputChangeProp = props.onInputChange, onMaxFreeSolo = props.onMaxFreeSolo, FormHelperTextPropsProp = props.FormHelperTextProps, action = props.action, filterOptions = props.filterOptions, renderOption = props.renderOption, renderTags = props.renderTags, getOptionDisabled = props.getOptionDisabled, renderGroup = props.renderGroup, onGroupExpanded = props.onGroupExpanded, renderNoOptions = props.renderNoOptions, InputPropsProp = props.InputProps, debug = props.debug, disabledItemsHighlightable = props.disabledItemsHighlightable, openProp = props.open, onOpen = props.onOpen, onClose = props.onClose, focused = props.focused, color = props.color, rest = __rest(props, ["itemToString", "keyToChips", "limitOfFreeChips", "minRowHeight", "suggestionItems", "messageRef", "MenuItem", "inputLabel", "nameError", "maxLength", "InputItem", "emailError", "autoSwitchEmail", "enableFreeChips", "inputPlaceholder", "errorSelectedItems", "enableAutoTransform", "allowPlainHelperText", "TextFieldProps", "selectedItems", "automationId", "screenReader", "onSelectChange", "variant", "groupExpanded", "groupDefaultExpanded", "groupVariant", "getExpandIconProps", "groupBy", "value", "autoHighlight", "fullWidth", "clearBtn", "toggleButton", "multiple", "clearButtonProps", "onClear", "ToggleButtonProps", "getToggleButtonProps", "inputValue", "onKeyDown", "helperText", "options", "openOnFocus", "toggleWithInput", "getOptionLabel", "renderInput", "label", "inputRef", "error", "placeholder", "freeSolo", "keyToTags", "maxFreeSolo", "SuggestionListProps", "autoSelect", "PopperComponent", "PopperProps", "initialIsOpen", "disabled", "required", "defaultIsOpen", "disableCloseOnSelect", "onChange", "onInputChange", "onMaxFreeSolo", "FormHelperTextProps", "action", "filterOptions", "renderOption", "renderTags", "getOptionDisabled", "renderGroup", "onGroupExpanded", "renderNoOptions", "InputProps", "debug", "disabledItemsHighlightable", "open", "onOpen", "onClose", "focused", "color"]);
    var _9 = __read(useState('bottom-start'), 2), position = _9[0], setPosition = _9[1];
    var innerInputRef = useRef(null);
    var textFieldRef = useRef(null);
    var inputRef = useForkRef(inputRefProp, innerInputRef);
    var inputContainerRef = useRef(null);
    // * if that have pass old suggestionItems mean that use old logic
    var isNew = !suggestionItems;
    var transitionDuration = transitionDurationProp === 'auto' ? undefined : transitionDurationProp;
    var anchorElRef = anchorElType === 'input' ? inputContainerRef : textFieldRef;
    var oneOfTagError = false;
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDownshiftError({ isNew: isNew, MenuItem: MenuItem, InputItem: InputItem });
    }
    var required = requiredProp || (TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.required);
    var _10 = useDownshift({
        focused: focused,
        open: openProp,
        variant: variant,
        onOpen: onOpen,
        onClose: onClose,
        wrapperRef: textFieldRef,
        inputContainerRef: inputContainerRef,
        inputRef: innerInputRef,
        value: valueProp,
        inputValue: inputValueProp,
        getOptionDisabled: getOptionDisabled,
        groupExpanded: groupVariant === 'normal' ? true : groupExpanded,
        groupDefaultExpanded: groupDefaultExpanded,
        getExpandIconProps: getExpandIconProps,
        options: suggestionItems || options,
        freeSolo: freeSolo,
        label: label,
        multiple: multiple,
        keyToTags: keyToTags,
        maxFreeSolo: maxFreeSolo,
        autoSelect: autoSelect,
        disableCloseOnSelect: disableCloseOnSelect,
        initialIsOpen: initialIsOpen,
        disabled: disabled,
        required: required,
        openOnFocus: openOnFocus,
        toggleWithInput: toggleWithInput,
        autoHighlight: autoHighlight,
        groupBy: groupBy,
        groupVariant: groupVariant,
        onChange: onChangeProp,
        onInputChange: onInputChangeProp,
        getOptionLabel: getOptionLabel,
        filterOptions: filterOptions,
        onMaxFreeSolo: onMaxFreeSolo,
        renderNoOptions: renderNoOptions,
        onKeyDown: onKeyDownProp,
        disabledItemsHighlightable: disabledItemsHighlightable,
        onGroupExpanded: onGroupExpanded,
    }), focusInput = _10.focusInput, optionItems = _10.optionItems, optionsGroupList = _10.optionsGroupList, selectedItems = _10.tags, highlightedIndex = _10.highlightedIndex, getToggleButtonProps = _10.getToggleButtonProps, getTagProps = _10.getTagProps, getTagListBoxProps = _10.getTagListBoxProps, getMenuProps = _10.getMenuProps, getPopperProps = _10.getPopperProps, getInputProps = _10.getInputProps, getInputAriaProps = _10.getInputAriaProps, getLabelProps = _10.getLabelProps, getItemProps = _10.getItemProps, isOpen = _10.isOpen, inputValue = _10.inputValue, activeIndex = _10.activeIndex, setActiveIndex = _10.setActiveIndex, setHighlightedIndex = _10.setHighlightedIndex, onInputChange = _10.onInputChange, keepHighlightedIndex = _10.keepHighlightedIndex, changeHighlightedIndexReason = _10.changeHighlightedIndexReason, closeMenu = _10.closeMenu, openMenu = _10.openMenu, reset = _10.reset, getClearButtonProps = _10.getClearButtonProps, noOptionItem = _10.noOptionItem, getNoOptionsProps = _10.getNoOptionsProps, isKeepHighlightedIndex = _10.isKeepHighlightedIndex, isDownshiftFocused = _10.focused, autoCompleteSelectedIndex = _10.autoCompleteSelectedIndex, popperElementRef = _10.popperElementRef;
    var open = Boolean((isOpen || noOptionItem) && textFieldRef.current);
    var _11 = getInputProps(), onBlur = _11.onBlur, InputProps = __rest(_11, ["onBlur"]);
    var helperText = isNew
        ? helperTextProp
        : nameError || allowPlainHelperText
            ? helperTextProp
            : undefined;
    var describedbyId = InputProps.id + "-helper-text";
    var FormHelperTextProps = FormHelperTextPropsProp || {};
    var screenReaderText = useMemo(function () {
        if (!screenReader || !selectedItems.length)
            return;
        var entry = screenReader.entry, entries = screenReader.entries;
        return ("" + (selectedItems.length === 1 ? entry : entries)).trim();
    }, [screenReader, selectedItems.length]);
    if (screenReaderText) {
        if (!helperText) {
            InputProps['aria-describedby'] = describedbyId;
        }
        else if (helperText) {
            FormHelperTextProps['aria-label'] =
                (screenReaderText + " " + helperText).trim();
        }
    }
    var toTextFieldRef = useForkRef(textFieldRef, ref);
    var colorHex = useMemo(function () { return (color ? getParsePaletteColor(color)({ theme: theme }) : undefined); }, [color, theme]);
    var startAdornment = (function () {
        if (isAutocomplete) {
            return undefined;
        }
        var getCustomizedTagProps = function (selectedItem, index) {
            var tagProps = getTagProps({
                selectedItem: selectedItem,
                index: index,
                label: getOptionLabel(selectedItem),
                disabled: disabled,
            });
            if (selectedItem.error) {
                oneOfTagError = true;
            }
            return __assign(__assign({}, tagProps), { color: colorHex });
        };
        return renderTags
            ? renderTags(selectedItems, getCustomizedTagProps)
            : selectedItems.map(function (selectedItem, index) {
                var _a;
                var itemChipProps = getCustomizedTagProps(selectedItem, index);
                // TODO: here should be remove
                if (InputItem) {
                    // TODO: should remove when all migrate
                    var isError = (emailError && emailError === ((_a = selectedItem.label) === null || _a === void 0 ? void 0 : _a.trim())) ||
                        (selectedItem.id && (errorSelectedItems === null || errorSelectedItems === void 0 ? void 0 : errorSelectedItems.includes(selectedItem.id))) ||
                        selectedItem.isError ||
                        selectedItem.error;
                    return (React.createElement(InputItem, __assign({}, selectedItem, itemChipProps, { isError: isError })));
                }
                return (React.createElement(RcChip, __assign({}, itemChipProps, { deleteIconProps: {
                        'aria-label': 'remove',
                    } })));
            });
    })();
    var endAdornment = (toggleButton || clearBtn) && (React.createElement(EndAdornment, null,
        clearBtn && (React.createElement(ClearIconButton, __assign({}, getClearButtonProps(clearButtonProps)))),
        toggleButton && (React.createElement(ArrowDownButton, __assign({ variant: "plain", "aria-hidden": true, color: isDownshiftFocused
                ? color || 'interactive.f01'
                : error
                    ? 'danger.f02'
                    : 'neutral.f04', size: "large" }, getToggleButtonProps(__assign(__assign({}, ToggleButtonProps), getToggleButtonPropsProp(isOpen))))))));
    var _12 = (function () {
        var _a = InputPropsProp || {}, classes = _a.classes, restInputPropsProp = __rest(_a, ["classes"]);
        var toClasses = combineClasses(classes, RcDownshiftInputClasses);
        return {
            containerClassName: toClasses.container,
            TextFieldInputProps: combineProps(__assign(__assign(__assign({ classes: omit(toClasses, ['container']), endAdornment: endAdornment, inputComponent: RcDownshiftInput }, getTagListBoxProps()), InputProps), { onBlur: debug ? undefined : onBlur }), restInputPropsProp),
        };
    })(), containerClassName = _12.containerClassName, TextFieldInputProps = _12.TextFieldInputProps;
    useImperativeHandle(action, function () { return ({
        getActiveIndex: function () { return activeIndex; },
        setActiveIndex: setActiveIndex,
        getFilterResultItems: function () { return optionItems; },
        getHighlightedIndex: function () { return highlightedIndex; },
        setHighlightedIndex: function (index) {
            return setHighlightedIndex(index, { reason: 'auto', reRender: true });
        },
        closeMenu: closeMenu,
        openMenu: openMenu,
        reset: reset,
        clearInput: function () { return onInputChange('', false); },
        keepHighlightedIndex: function () { return keepHighlightedIndex(); },
        focus: focusInput,
    }); });
    var hasTags = selectedItems.length > 0;
    var isRenderNoOptions = !!noOptionItem;
    var popperRef = useRef(null);
    var handleUpdatePopper = useEventCallback(function () {
        var _a;
        (_a = popperRef.current) === null || _a === void 0 ? void 0 : _a.update();
    });
    var menuChildren = (
    // * that root element for animation host
    React.createElement(React.Fragment, null,
        isOpen && (React.createElement(RcSuggestionList, __assign({ selectedIndex: isAutocomplete ? autoCompleteSelectedIndex : undefined, highlightedIndex: highlightedIndex, optionsGroupList: optionsGroupList, options: optionItems, groupVariant: groupVariant, groupExpanded: groupExpanded, renderGroup: renderGroup, MenuItem: MenuItem, renderOption: renderOption, inputValue: inputValue, getItemProps: getItemProps, getMenuProps: getMenuProps, changeHighlightedIndexReason: changeHighlightedIndexReason, getOptionDisabled: getOptionDisabled, isKeepHighlightedIndex: isKeepHighlightedIndex, onUpdatePopper: handleUpdatePopper, maxContainerHeight: 180, getOptionLabel: getOptionLabel, position: "unset", virtualize: virtualize }, SuggestionListProps))),
        isRenderNoOptions && (renderNoOptions === null || renderNoOptions === void 0 ? void 0 : renderNoOptions(getNoOptionsProps, noOptionItem))));
    var prevMenuChildren = usePrevious(function () { return menuChildren; });
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledTextField, __assign({ renderInput: renderInput, hasTags: hasTags, ref: toTextFieldRef, inputRef: inputRef, "data-test-automation-id": automationId, fullWidth: fullWidth, placeholder: !hasTags ? placeholder : undefined, label: label, focused: isDownshiftFocused, disabled: disabled, required: required, color: color, 
            // * if below one of tag is error, that main downshift default will be error
            error: error !== null && error !== void 0 ? error : oneOfTagError, FormHelperTextProps: FormHelperTextProps, InputProps: TextFieldInputProps, helperText: helperText }, combineProps({
            InputLabelProps: getLabelProps(),
            inputProps: __assign({ startAdornment: startAdornment,
                containerClassName: containerClassName,
                maxLength: maxLength, containerRef: inputContainerRef }, getInputAriaProps(TextFieldProps === null || TextFieldProps === void 0 ? void 0 : TextFieldProps.inputProps)),
        }, rest), { value: inputValue, clearBtn: false })),
        !helperText && screenReaderText && (React.createElement(RcVisuallyHidden, { id: describedbyId }, screenReaderText)),
        React.createElement(StyledPopper, __assign({ ref: popperElementRef, open: open, position: position, component: PopperComponent, placement: "bottom-start", anchorEl: anchorElRef.current, "data-test-automation-id": "suggestions-list", popperRef: popperRef, 
            // * view type in popper.js
            popperOptions: {
                onUpdate: function (e) {
                    var currPosition = e.placement;
                    if (position !== currPosition) {
                        setPosition(currPosition);
                    }
                },
            } }, getPopperProps(PopperProps), { transition: popperTransition }), popperTransition
            ? function (_a) {
                if (_a === void 0) { _a = {}; }
                var _b = _a.TransitionProps, _c = _b === void 0 ? {} : _b, onEnterProp = _c.onEnter, onExitProp = _c.onExited, inProp = _c.in, restTransitionProp = __rest(_a, ["TransitionProps"]);
                var onEnter = function (node, isAppearing) {
                    var _a;
                    onEnterProp();
                    (_a = TransitionPropsProp.onEnter) === null || _a === void 0 ? void 0 : _a.call(TransitionPropsProp, node, isAppearing);
                };
                var onExited = function (node) {
                    var _a;
                    onExitProp();
                    (_a = TransitionPropsProp.onExited) === null || _a === void 0 ? void 0 : _a.call(TransitionPropsProp, node);
                };
                return (React.createElement(TransitionComponent, __assign({ in: inProp, onEnter: onEnter, onExited: onExited }, restTransitionProp, { timeout: transitionDuration }, (virtualize ? {} : DEFAULT_GROW_STYLE), TransitionPropsProp),
                    React.createElement("div", null, 
                    // when menu be close use previous option menu,
                    // keep menu item before animation done
                    inProp ? menuChildren : prevMenuChildren)));
            }
            : menuChildren)));
}));
var RcDownshift = styled(_RcDownshift)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), DownshiftStyle);
RcDownshift.defaultProps = {};
RcDownshift.displayName = 'RcDownshift';
var ExportType = RcDownshift;
export { ExportType as RcDownshift, RcDownshiftDefaultFilterOptions, RcDownshiftInput, };
var templateObject_1;
